import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { DataFieldTypes } from '@app/enums/data-field-types.enum';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FieldDetailsForm } from '@forms/settings/components';
import { DataFieldGroup } from '@models/settings/data-field-group.model';
import { DataField } from '@models/settings/data-field.model';
import { DataFieldOptionStateManager } from '@settings/classes/data-field-option-state-manager.class';

@Component({
    templateUrl: './fields-store.template.html',
})
export class FieldsStoreView {
    @ViewChild('detailsForm') detailsForm: FieldDetailsForm;
    isLoading = false;
    title = 'settings.custom-fields.createCustomField';
    onEditCustomFieldPage = false;
    field: DataField = new DataField({ isRequired: false, isEncrypted: false });
    fieldGroup: DataFieldGroup = new DataFieldGroup();
    dataFieldOptionState: DataFieldOptionStateManager;

    constructor(
        private auth: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private analyticsService: AnalyticService,
        private notify: NotifyService
    ) {
        // On edit custom field information flow, we need to fetch the field.
        if (this.route.snapshot.data.models) {
            const field = this.route.snapshot.data.models.get('field');

            if (!field) {
                return;
            }

            this.title = 'settings.custom-fields.editCustomField';
            this.field = field;
            this.fieldGroup = field.dataFieldGroup;
            this.onEditCustomFieldPage = true;
        }

        this.dataFieldOptionState = new DataFieldOptionStateManager(this.field, this.auth.company.id);
    }

    async onSave(): Promise<void> {
        this.detailsForm.submit();

        if (this.detailsForm.form.invalid) {
            return;
        }

        this.isLoading = true;

        if (this.field.type === DataFieldTypes.dropdown && this.dataFieldOptionState.getSavableOptions().length === 0) {
            this.notify.error('settings.custom-fields.cannotSaveDropdownWithoutOptions');
            this.isLoading = false;
            return;
        }

        try {
            if (!this.fieldGroup.isPersisted) {
                const fieldGroup = await this.fieldGroup.param('company', this.auth.company.id).save();
                this.fieldGroup = fieldGroup;
            }

            this.field.dataFieldGroupId = this.fieldGroup.id;
            const field = await this.field.param('company', this.auth.company.id).save();

            if (this.field.type === DataFieldTypes.dropdown) {
                await this.dataFieldOptionState.delete();
                await this.dataFieldOptionState.save();
            }

            this.setPendoEvents(field.type);
            this.setNotifyMessage();
            this.navigateUser(field);
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private navigateUser(field: DataField): void {
        if (this.onEditCustomFieldPage) {
            this.router.navigate(['/settings', 'custom-fields', 'fields', field.id]);
            return;
        } else {
            this.router.navigate(['/settings', 'custom-fields', 'fields', field.id, 'assign-employees']);
        }
    }

    private setNotifyMessage(): void {
        if (this.onEditCustomFieldPage) {
            this.notify.success('settings.custom-fields.customFieldUpdated');
            return;
        } else {
            this.notify.success('settings.custom-fields.customFieldCreated');
        }
    }

    private setPendoEvents(fieldType: string): void {
        if (this.onEditCustomFieldPage) {
            this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleEditCustomField, {
                customFieldType: fieldType,
            });
            return;
        } else {
            this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleCreateCustomField, {
                customFieldType: fieldType,
            });
        }
    }
}
